import React, { useState, useEffect, useRef } from "react";
import { applicationError } from "../../redux/actions/error";

const useConnectionMonitor = (active) => {
  const [downlink, setDownlink] = useState(1);

  useEffect(() => {
    if (!active) return;
    let runFor = 10;
    const id = setInterval(() => {
      if (runFor <= 0) { clearInterval(id); return; }
      const conn =
        window.navigator.connection ||
        window.navigator.mozConnection ||
        window.navigator.webkitConnection;
      setDownlink(conn ? conn.downlink : 1);
      runFor--;
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  return downlink;
};

const LoadingModal = ({ loading, message }) => {
  const [visible, setVisible] = useState(loading);
  const [fading, setFading] = useState(false);
  const timeoutRef = useRef(null);
  const fadeRef = useRef(null);
  const downlink = useConnectionMonitor(visible);

  useEffect(() => {
    if (loading) {
      setVisible(true);
      setFading(false);
      timeoutRef.current = setTimeout(() => {
        applicationError({ message: "Loading timeout", err: {}, errorCode: "loadingTimeout" });
      }, 30000);
    } else {
      clearTimeout(timeoutRef.current);
      setFading(true);
      fadeRef.current = setTimeout(() => {
        setVisible(false);
        setFading(false);
      }, 450);
    }
    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(fadeRef.current);
    };
  }, [loading]);

  if (!visible) return null;

  const connectionMsg =
    downlink === 0 ? "No internet connection" :
    downlink < 1 ? "Slow internet connection" :
    null;

  return (
    <div style={{ ...styles.overlay, opacity: fading ? 0 : 1, transition: "opacity 0.45s ease" }}>
<div style={styles.content}>
        <div style={styles.ringWrap}>
          <div style={styles.ringOuter} className="lm-ring-outer" />
          <div style={styles.ringInner} className="lm-ring-inner" />
          <div style={styles.dot} className="lm-dot" />
        </div>
        <p style={styles.label}>{message || "Loading Dashboard"}</p>
        {connectionMsg && (
          <p style={styles.connMsg}>{connectionMsg}</p>
        )}
      </div>
      <style>{css}</style>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    position: "relative",
    zIndex: 1,
  },
  ringWrap: {
    position: "relative",
    width: "72px",
    height: "72px",
  },
  ringOuter: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "3px solid transparent",
    borderTopColor: "var(--main-color, #80000b)",
    borderRightColor: "var(--main-color, #80000b)",
  },
  ringInner: {
    position: "absolute",
    inset: "10px",
    borderRadius: "50%",
    border: "2px solid transparent",
    borderBottomColor: "color-mix(in srgb, var(--main-color, #80000b) 55%, transparent)",
    borderLeftColor: "color-mix(in srgb, var(--main-color, #80000b) 55%, transparent)",
  },
  dot: {
    position: "absolute",
    inset: "28px",
    borderRadius: "50%",
    background: "var(--main-color, #80000b)",
    opacity: 0.9,
  },
  label: {
    margin: 0,
    color: "#1a1a1a",
    fontSize: "15px",
    fontWeight: 500,
    letterSpacing: "0.02em",
  },
  connMsg: {
    margin: 0,
    color: "#888888",
    fontSize: "13px",
    letterSpacing: "0.01em",
  },
};

const css = `
  @keyframes lm-spin-cw {
    to { transform: rotate(360deg); }
  }
  @keyframes lm-spin-ccw {
    to { transform: rotate(-360deg); }
  }
  @keyframes lm-pulse {
    0%, 100% { transform: scale(0.7); opacity: 0.7; }
    50% { transform: scale(1); opacity: 1; }
  }
.lm-ring-outer {
    animation: lm-spin-cw 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }
  .lm-ring-inner {
    animation: lm-spin-ccw 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }
  .lm-dot {
    animation: lm-pulse 1.4s ease-in-out infinite;
  }
`;

export default LoadingModal;
